import type { ResourceStoreData } from '@atlassian/react-resource-router';
import { RESOURCE_TYPE_WORKSPACE_CONTEXT } from './constants.tsx';
import type {
	AdminSettingsResponse,
	AdminSettingsMap,
	BannerSettings,
	BannerSettingsResponse,
} from './types.tsx';

export function extractResourceStatePromise(resourceState: ResourceStoreData) {
	return resourceState?.[RESOURCE_TYPE_WORKSPACE_CONTEXT]?.state?.promise;
}

/**
 * Returns the appropriate admin settings URL based on execution context.
 *
 * @param workspaceId - The workspace identifier
 * @returns Server-side URL (/api/v1/admin/settings) when running in SSR context,
 *          or client-side gateway URL when running in browser
 */
export const getAdminSettingsUrl = (workspaceId: string) => {
	return __SERVER__
		? '/api/v1/admin/settings'
		: `/gateway/api/jsm/assets/workspace/${workspaceId}/v1/admin/settings`;
};

/**
 * Returns the appropriate banner settings URL based on execution context.
 *
 * @param workspaceId - The workspace identifier
 * @returns Server-side URL (/api/v1/admin/banner-settings) when running in SSR context,
 *          or client-side gateway URL when running in browser
 */
export const getBannerSettingsUrl = (workspaceId: string) => {
	return __SERVER__
		? '/api/v1/admin/banner-settings'
		: `/gateway/api/jsm/assets/workspace/${workspaceId}/v1/admin/banner-settings`;
};

export const transformAdminSettingsResponse = (
	response: AdminSettingsResponse,
): AdminSettingsMap => {
	return response.reduce((acc: AdminSettingsMap, setting) => {
		acc[setting.key] = setting;
		return acc;
	}, {});
};

/**
 * Transforms banner settings by ensuring content is properly parsed.
 * Handles both string (JSON) and object content formats.
 *
 * @param bannerSettings - The banner settings response or settings object
 * @returns Transformed banner settings with parsed content
 */
export const transformBannerSettings = (
	bannerSettings: BannerSettingsResponse | BannerSettings,
) => {
	const { content, ...rest } = bannerSettings;
	const isContentString = content && typeof content === 'string';

	return {
		...rest,
		content: isContentString ? JSON.parse(content) : JSON.stringify(content),
	};
};
